import { z } from 'zod';
import { logger } from '@/lib/utils/logger';
import type { Analysis } from '@/entities/analysis';
import type { IqDataSource } from '@/entities/iq-data-source';

/**
 * The upload metadata both data-source blobs carry, read through one schema.
 *
 * Parsed rather than read off the generated types because the two blobs reach the client
 * differently: YouGov as a typed DTO, Pathmatics as an open record the report sections parse
 * themselves. The metadata field names are identical, so one schema serves both.
 *
 * `assetId` and `fileSizeBytes` are optional: data attached before uploads were stored has
 * neither, which is why download is offered per file rather than per source.
 */
const attachmentSchema = z.object({
  sourceFilename: z.string(),
  rowCount: z.number(),
  uploadedAt: z.string(),
  uploadedBy: z.string(),
  assetId: z.string().optional(),
  fileSizeBytes: z.number().optional(),
});

export type DataSourceAttachment = z.infer<typeof attachmentSchema>;

export const attachmentFor = (
  analysis: Analysis,
  source: IqDataSource,
): DataSourceAttachment | null => {
  const blob = source === 'yougov' ? analysis.yougovData : analysis.pathmaticsData;
  if (!blob) return null;

  const parsed = attachmentSchema.safeParse(blob);
  if (!parsed.success) {
    // Distinct from `!blob` above: the report does carry data, in a shape this client cannot
    // read. Returning null silently would hide the filename, the download and the remove
    // action, and let the next upload skip its replace confirmation.
    logger.error('Unreadable data-source attachment', { source, error: parsed.error });
    return null;
  }
  return parsed.data;
};
